import * as Actions from '../actions/privilegeActions';
import Privilege from '../../entities/privilege';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type PrivilegeAction = { 
    type: string; 
    privilege: Privilege | null; 
    privilegeId: string | Privilege; 
    partial: boolean; 
    lastFetch: number; 
    privileges: Privilege[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Privilege> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Privilege[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Privilege> | null, firmId: string, userId: string, updates?: Privilege[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'privilege', 'privileges', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Privilege>} data
 * @param {Privilege[]} [updates]
 * @returns {ArrayMap<Privilege>}
 */
function updateStorage(data: ArrayMap<Privilege>, updates?: Privilege[]): ArrayMap<Privilege> {
	if (logging) console.debug('⮂ privilegeReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Privilege[]} [updates]
 * @returns {ArrayMap<Privilege>}
 */
function updateStorageNoReturn(updates?: Privilege[]): void {
	if (logging) console.debug('⮂ privilegeReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Privilege[]} [data]
 * @returns {ArrayMap<Privilege>}
 */
function createState(data?: Privilege[] | ArrayMap<Privilege>): ArrayMap<Privilege> {
	if (!data) return ArrayMapRegistry.clone<Privilege>('privilege', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Privilege>('privilege', data.map(e => e));
    else return ArrayMapRegistry.clone<Privilege>('privilege', data);
}

function privileges(state: ArrayMap<Privilege>, action: PrivilegeAction): ArrayMap<Privilege> {
	if (!state) state = createState();

	let st: ArrayMap<Privilege>;

	switch (action.type) {
		// action.privilege - the new value of privilege with id action.id
		// action.privilegeId - id of the target, used if new value is null
		case Actions.PRIVILEGE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ privilegeReducer: FETCH_ONE_SUCCESS', action);

			if (action.privilege === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.privilegeId)) {
				st = state.clone();
				st.set(action.privilege);
				// state.clear();

				updateStorageNoReturn([action.privilege]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.privileges
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.privileges
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.privileges
			 !partial, !stateEmpty, fetchEmpty	-> D return privileges in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge privileges in state newer than those in action.privileges
		 */
		case Actions.PRIVILEGE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.privileges.length === 0;
			const fetched = action.privileges.map(privilege => new Privilege({...privilege, persisted: true}));

			if (logging) console.debug(`⮂ privilegeReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} privileges with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ privilegeReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ privilegeReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Privilege> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Privilege[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(privilege => {
					const current = st.get(privilege.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && privilege.status === 'D') {
							// Remove it from state
							toUpdate.push(privilege);
							st.delete(privilege);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (privilege.addedOn > current.addedOn) {
								toUpdate.push(privilege);
								st.set(privilege);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (privilege.status !== 'D') {
							toUpdate.push(privilege);
							st.set(privilege);
						}
						else if (logging) console.debug('⮂ privilegeReducer: partialMerge: skipping deleted privilege not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ privilegeReducer: empty: privileges', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ privilegeReducer: fullMerge: privileges', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localPrivilege => {
                    // If this privilege has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localPrivilege.persisted) {
                        const fetchedPrivilege = st.get(localPrivilege.id);
                        if (!fetchedPrivilege || localPrivilege.addedOn > fetchedPrivilege.addedOn) st.set(localPrivilege);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.PRIVILEGE_FETCH_ONE_FAILURE:
			console.error('PRIVILEGE_FETCH_ONE_FAILURE', action.privilegeId, action.err);

			// No update to storage required
			return state;

		case Actions.PRIVILEGE_FETCH_FAILURE:
			console.error('PRIVILEGE_FETCH_FAILURE', action.privilegeId, action.err);

			// No update to storage required
			return state;

		case Actions.PRIVILEGE_RESTORE: {
		    let privilege = new Privilege({...action.privilege});
            st = state.clone();

            const existing = st.get(privilege.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (privilege.addedOn >= existing.addedOn) {
                    st.set(privilege);
                    return updateStorage(st, [privilege]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (privilege.status === 'D') return state;

                st.set(privilege);
                return updateStorage(st, [privilege]);
            }
        }
            
		case Actions.PRIVILEGE_SAVE: {
		    // console.debug('⮂ PRIVILEGE_SAVE reducer', action.privilege?.id);
		    
			// clone the privilege
			let privilege = new Privilege({...action.privilege});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new privilege in the state
			// If this is a create, add to the queue
			const _privilege = st.get(privilege.id);

			if (!_privilege) st.set(privilege);
			else {
				if (privilege.addedOn >= _privilege.addedOn) st.set(privilege);
			}

			return updateStorage(st, [privilege]);
		}

		case Actions.PRIVILEGE_SAVE_MANY: {
		    if (logging) console.debug('⮂ PRIVILEGE_SAVE_MANY reducer', action.privileges.map(privilege => privilege.id).join(','));
		    
			// clone the privileges
			let toProcess = action.privileges.map(privilege => new Privilege(privilege));

			st = state.clone();
			// state.clear();

			const updates: Privilege[] = [];

			toProcess.forEach(privilege => {
				// Add or update the new privilege in the state
				// If this is a create, add to the queue
				const existing = st.get(privilege.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (privilege.status !== 'D') {
						st.push(privilege);
						updates.push(privilege);
					}
					else if (logging) console.debug('⮂ PRIVILEGE_SAVE_MANY: skipping deleted privilege not in state');
				}
				else {
					if (privilege.addedOn >= existing.addedOn) {
						st.set(privilege);
						updates.push(privilege);
					}
					else if (logging) console.debug('⮂ Skipping, existing privilege had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.PRIVILEGE_REMOVE: {
			// clone the privilege
			const privilege = new Privilege({...action.privilege});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(privilege);

			return updateStorage(st, [privilege]);
		}

		case Actions.PRIVILEGE_RESTORE_FAILURE:
		case Actions.PRIVILEGE_SAVE_FAILURE: {
		    console.warn('PRIVILEGE_SAVE_FAILURE reducer', action.privilege?.id);
		    if (!action.privilege?.id) {
				console.warn('PRIVILEGE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const privilege = ensure(Privilege, action.privilege);
			if (!privilege) {
				console.error('Could not find privilege after call to save');
				return state;
			}

			st = state.clone();

			const _privilegeRef = state.get(privilege.id);

			if (_privilegeRef && (_privilegeRef.addedOn === privilege.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _privilege = new Privilege({..._privilegeRef});
				_privilege.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_privilege.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('privilege', _privilege).then(r => {
						if (r.isErr()) console.error(`⮂ privilegeReducer: failed to save privilege with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ privilegeReducer: saved privilege with fatalError info');
					}).catch(err => console.error(`⮂ privilegeReducer: failed to save privilege with fatalError info: ${err.message}`));

				st.set(_privilege);
			}

			return updateStorage(st);
		}

		case Actions.PRIVILEGE_SAVE_MANY_FAILURE: {
		    console.warn('PRIVILEGE_SAVE_MANY_FAILURE reducer', action.privileges.map(privilege => privilege.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.PRIVILEGE_RESTORE_SUCCESS:
		case Actions.PRIVILEGE_SAVE_SUCCESS: {
			if (!action.privilege?.id) {
				console.warn('PRIVILEGE_SAVE_SUCCESS reducer');
                return state;
			}

			const privilegeRef = state.get(action.privilege?.id);
			if (!privilegeRef) {
				console.warn('Could not find privilege after call to save');
                return state;
			}

			st = state.clone();

			if (action.privilege.addedOn >= privilegeRef.addedOn) {
				st.set(action.privilege);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const privilege = new Privilege({...privilegeRef, persisted: true});
				st.set(privilege);
			}

			return updateStorage(st, [action.privilege]);
		}

		case Actions.PRIVILEGE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ PRIVILEGE_SAVE_MANY_SUCCESS reducer', action.privileges.map(privilege => privilege.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const privileges: Privilege[] = [];

			action.privileges.forEach(e => {
				const privilegeRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!privilegeRef) console.warn('Could not find privilege after call to save');
				else if (e.addedOn >= privilegeRef.addedOn) {
				    st.set(e);
				    privileges.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const privilege = new Privilege({...privilegeRef, persisted: true});
					st.set(privilege);
					privileges.push(privilege);
				}
			});

			return updateStorage(st, privileges);
		}

		case Actions.PRIVILEGE_REMOVE_FAILURE: {
		    console.warn('PRIVILEGE_REMOVE_FAILURE action', action.privilege?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.PRIVILEGE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.PRIVILEGE_RESTORE_MANY: {
			if (logging) console.debug('⮂ PRIVILEGE_RESTORE_MANY reducer', action.privileges.map(privilege => privilege.id).join(','));

			const toProcess = action.privileges.map(privilege => new Privilege({...privilege}));
			st = state.clone();

			const updates: Privilege[] = [];

			toProcess.forEach(privilege => {
				const existing = st.get(privilege.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (privilege.addedOn >= existing.addedOn) {
						st.set(privilege);
						updates.push(privilege);
					}
					else if (logging) console.debug('⮂ PRIVILEGE_RESTORE_MANY: skipping older version of privilege');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (privilege.status !== 'D') {
						st.set(privilege);
						updates.push(privilege);
					}
					else if (logging) console.debug('⮂ PRIVILEGE_RESTORE_MANY: skipping deleted privilege not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.PRIVILEGE_REMOVE_MANY: {
			if (logging) console.debug('⮂ PRIVILEGE_REMOVE_MANY reducer', action.privileges.map(privilege => privilege.id).join(','));

			const toProcess = action.privileges.map(privilege => new Privilege({...privilege}));
			st = state.clone();

			toProcess.forEach(privilege => {
				st.delete(privilege);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default privileges;